import dialogFunction from './DialogFunction';

// Member role tiers from lowest to highest. A "downgrade" is moving to a
// lower tier (e.g. Manufacturer -> Spark, Manufacturer -> Restricted,
// Spark -> Restricted). Only these known tiers are considered; any unknown
// or custom role never triggers the downgrade warning.
export const ROLE_HIERARCHY = ['Restricted', 'Spark', 'Manufacturer'];

// Match tolerant of casing / surrounding whitespace, since role-name strings
// come from the backend and may not be exactly cased like ROLE_HIERARCHY.
const normalizeRole = (role) =>
  typeof role === 'string' ? role.trim().toLowerCase() : role;

const NORMALIZED_HIERARCHY = ROLE_HIERARCHY.map(normalizeRole);

const getRoleTierIndex = (role) =>
  NORMALIZED_HIERARCHY.indexOf(normalizeRole(role));

export const CONFIRM_ROLE_DOWNGRADE_CONTENT =
  "Downgrading this Member's role breaks defined SPINS IX-ONE business processes. Are you sure you'd like to continue?";

/**
 * True when assigning `toRole` to a member currently on `fromRole` is a
 * downgrade. Both roles must be known tiers; otherwise it is never a downgrade.
 */
export const isRoleDowngrade = (fromRole, toRole) => {
  const fromIndex = getRoleTierIndex(fromRole);
  const toIndex = getRoleTierIndex(toRole);

  if (fromIndex < 0 || toIndex < 0) return false;

  return toIndex < fromIndex;
};

/** True when assigning `toRole` downgrades ANY of the given current roles. */
export const isAnyRoleDowngrade = (fromRoles = [], toRole) =>
  fromRoles.some((fromRole) => isRoleDowngrade(fromRole, toRole));

/**
 * Shared "Confirm Role Downgrade" warning dialog.
 *
 * @param {Object} params
 * @param {React.ReactNode} [params.content] - override the default warning copy
 * @param {Function} [params.onOk] - proceed with the downgrade
 * @param {Function} [params.onCancel] - revert / abort the downgrade
 */
const confirmRoleDowngrade = ({
  content = CONFIRM_ROLE_DOWNGRADE_CONTENT,
  onOk,
  onCancel,
  ...rest
} = {}) =>
  dialogFunction({
    type: 'warn',
    content,
    okText: 'OK',
    cancelText: 'Cancel',
    onOk,
    onCancel,
    ...rest,
  });

/**
 * Shows the downgrade warning only when assigning `toRole` actually downgrades
 * one of `fromRoles`. Otherwise proceeds straight to `onOk` without prompting.
 *
 * @param {Object} params
 * @param {string|string[]} params.fromRoles - current role(s) of the member(s)
 * @param {string} params.toRole - the role being assigned
 * @param {Function} [params.onOk]
 * @param {Function} [params.onCancel]
 * @param {React.ReactNode} [params.content]
 */
export const confirmRoleDowngradeIfNeeded = ({
  fromRoles,
  toRole,
  onOk,
  onCancel,
  content,
}) => {
  const fromList = Array.isArray(fromRoles) ? fromRoles : [fromRoles];

  if (isAnyRoleDowngrade(fromList, toRole)) {
    return confirmRoleDowngrade({ content, onOk, onCancel });
  }

  return onOk?.();
};

export default confirmRoleDowngrade;
